import {useMemo, useState} from "react";
import _ from "lodash";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {useAtom, useAtomValue} from "jotai";
import dayjs from "dayjs";
import {Box, IconButton} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {MaterialReactTable, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {CompanyMemberInviteDialog} from "../dialogs/CompanyMemberInviteDialog";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {CompanyMemberItem} from "../../types/companyMemberItem";
import {useAccessMarker} from "../../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../../utils/accessControlUtils";
import {formatShortDate} from "../../utils/dateUtils";
import {EditIcon} from "../icons/EditIcon";
import {
    ALL,
    COMPANY_MEMBER_ROLES,
    COMPANY_MEMBER_TYPES,
    enumItemToTableFilterSelectOption,
    USER_TYPES,
} from "../../utils/enumItemsUtils";
import {useLocalizedEnumItems} from "../../hooks/localizedEnumItemsHooks";
import {TableFilterSelectOption} from "../../types/table";
import {User, UserGlobalRole} from "../../types/domain/user";
import {useResourceManagement} from "../../hooks/resourceHooks";
import {getUserById} from "../../services/userService";
import {CompanyMemberEditDialog} from "../dialogs/CompanyMemberEditDialog";
import {authenticatedUserLoadable, authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {CompanyMemberRole} from "../../types/domain/companyMember";
import {ErrorAlert} from "../base/ErrorAlert";

export interface Props {
    members: CompanyMemberItem[];
    isLoading: boolean;
    refreshMembers: () => void;
    companyId?: string;
}

export const CompanyMembersTable: React.FC<Props> = ({members, isLoading, refreshMembers, companyId}) => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const [editDialogOpened, setEditDialogOpened] = useState<boolean>(false);
    const [activeCompanyMemberItem, setActiveCompanyMemberItem] = useState<CompanyMemberItem | undefined>(undefined);
    const [activeUser, setActiveUser] = useState<User>();
    const userManagement = useResourceManagement({getUserById});
    const {items: memberRoleOptions} = useLocalizedEnumItems(MEMBER_ROLE_OPTIONS);
    const {items: memberTypeOptions} = useLocalizedEnumItems(MEMBER_TYPE_OPTIONS);
    const authenticatedUserState = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = authenticatedUserState.state === "hasData" ? authenticatedUserState.data : null;

    const [permissionError, setPermissionError] = useState(false);
    const [locale] = useAtom(authenticatedUserTableLocaleAtom);
    const defaultTableOptions = getDefaultMRTOptions<CompanyMemberItem>(locale);
    const {items: userTypeOptions} = useLocalizedEnumItems(USER_TYPE_OPTIONS);
    const columns = useMemo(
        () =>
            getTableColumns(
                t,
                memberRoleOptions.map(enumItemToTableFilterSelectOption),
                memberTypeOptions.map(enumItemToTableFilterSelectOption),
                userTypeOptions.map(enumItemToTableFilterSelectOption),
                dateFormat,
            ),
        [t, memberRoleOptions, memberTypeOptions, userTypeOptions, dateFormat],
    );

    const onEditMemberDialogClose = (isSuccessful: boolean) => {
        setEditDialogOpened(false);
        if (isSuccessful) {
            void refreshMembers();
            setActiveUser(undefined);
        }
    };

    const table = useMaterialReactTable({
        ...defaultTableOptions, //spread your default options

        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 20rem)",
                minHeight: "calc(100vh - 20rem)",
            },
        },
        initialState: {
            ...defaultTableOptions.initialState,
            columnFilters: [
                {
                    id: "role",
                    value: "ALL",
                },
                {
                    id: "type",
                    value: "ALL",
                },
                {
                    id: "advanced",
                    value: "ALL",
                },
            ],
        },
        state: {
            isLoading,
        },
        columns,
        data: members,
        renderRowActions: ({row}) => (
            <Box sx={{display: "flex", gap: "1rem"}}>
                <IconButton
                    color="primary"
                    onClick={() => {
                        const activeMember = row.original;
                        if (activeMember.membershipType === "Member") {
                            const activeUserId = activeMember.member.userId;
                            void userManagement
                                .getUserById(activeUserId)
                                .then((activeMemberData) => {
                                    if (!authenticatedUser?.security?.role) {
                                        return;
                                    }
                                    const userHasGlobalPermission = [
                                        UserGlobalRole.ADMIN,
                                        UserGlobalRole.CS,
                                        UserGlobalRole.HYDROSAT,
                                    ].includes(authenticatedUser.security.role);

                                    const activeCompanySecurity = authenticatedUser
                                        ? authenticatedUser.companySecurity.find(
                                              (companySecurity) => companySecurity.companyId === companyId,
                                          )
                                        : null;

                                    const userHasCompanyPermission =
                                        activeCompanySecurity &&
                                        [CompanyMemberRole.MANAGER, CompanyMemberRole.EDITOR].includes(
                                            activeCompanySecurity.role,
                                        );

                                    if (userHasGlobalPermission || userHasCompanyPermission) {
                                        setActiveUser(activeMemberData);
                                        setActiveCompanyMemberItem(activeMember);
                                        setEditDialogOpened(true);
                                    } else {
                                        setPermissionError(true);
                                    }
                                })
                                .catch(() => {
                                    setPermissionError(true);
                                });
                        } else {
                            setActiveCompanyMemberItem(activeMember);
                            setEditDialogOpened(true);
                        }
                    }}
                >
                    <EditIcon
                        sx={{
                            stroke: "#303A8C",
                            width: "1.125rem",
                            height: "1.125rem",
                        }}
                    />
                </IconButton>
            </Box>
        ),
    });

    return (
        <>
            {permissionError && (
                <ErrorAlert
                    message={t("permission.edit_denied")}
                    onClose={() => {
                        setPermissionError(false);
                    }}
                />
            )}
            <MaterialReactTable table={table} />
            {editDialogOpened &&
                activeUser !== undefined &&
                companyId !== undefined &&
                activeCompanyMemberItem?.membershipType === "Member" && (
                    <CompanyMemberEditDialog
                        mode="edit"
                        betaFeaturesEditEnabled={access.canEnableBetaFeatures}
                        managerRoleEnabled={access.canAssignManagerRole}
                        companyId={companyId}
                        member={activeCompanyMemberItem.member}
                        user={activeUser}
                        open={editDialogOpened}
                        onClose={onEditMemberDialogClose}
                    />
                )}
            {editDialogOpened && companyId !== undefined && activeCompanyMemberItem?.membershipType === "Invited" && (
                <CompanyMemberInviteDialog
                    mode="edit"
                    companyId={companyId}
                    invitation={activeCompanyMemberItem.invitation}
                    open={editDialogOpened}
                    onClose={onEditMemberDialogClose}
                />
            )}
        </>
    );
};

const MEMBER_ROLE_OPTIONS = [ALL, ...COMPANY_MEMBER_ROLES];
const MEMBER_TYPE_OPTIONS = [ALL, ...COMPANY_MEMBER_TYPES];
const USER_TYPE_OPTIONS = [ALL, ...USER_TYPES];

const getTableColumns = (
    t: TFunction<"translation", undefined>,
    memberRoleOptions: TableFilterSelectOption[],
    memberTypeOptions: TableFilterSelectOption[],
    userTypeOptions: TableFilterSelectOption[],
    format: string,
): MRT_ColumnDef<CompanyMemberItem>[] => [
    {
        id: "name",
        header: t("companies.members_table.name"),
        accessorFn: (row) => (row.membershipType === "Member" ? row.member.userName : row.invitation.userName),
        minSize: 100,
    },
    {
        id: "email",
        header: t("companies.members_table.email"),
        accessorFn: (row) => (row.membershipType === "Member" ? row.member.userEmail : row.invitation.userEmail),
        minSize: 100,
    },
    {
        id: "role",
        header: t("companies.members_table.role"),
        accessorKey: "role",
        minSize: 50,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: memberRoleOptions,
        filterFn: (row, _, filter) => filter === "ALL" || row.original.role === filter,
        Cell: ({row}) =>
            t(
                COMPANY_MEMBER_ROLES.find((t) => t.id.toString().toUpperCase() === row.original.role.toUpperCase())
                    ?.i18nKey ?? row.original.role,
            ),
    },
    {
        id: "type",
        header: t("companies.members_table.type"),
        accessorKey: "membershipType",
        minSize: 50,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: memberTypeOptions,
        filterFn: (row, _, filter) =>
            filter === "ALL" || row.original.membershipType.toLowerCase() === (filter as string).toLowerCase(),
        Cell: ({row}) =>
            t(
                COMPANY_MEMBER_TYPES.find(
                    (t) => t.id.toString().toUpperCase() === row.original.membershipType.toUpperCase(),
                )?.i18nKey ?? row.original.membershipType,
            ),
    },
    {
        id: "advanced",
        header: t("companies.members_table.advanced"),
        accessorFn: (row) => (row.membershipType === "Member" && row.member.isAdvanced ? true : false),
        Cell: ({row}) =>
            row.original.membershipType === "Member" && (
                <CheckIcon sx={{color: row.original.member.isAdvanced ? "#404DB8" : "#E9EBF1"}} />
            ),
        minSize: 50,
        size: 80,
        filterVariant: "select",
        filterSelectOptions: userTypeOptions,
        filterFn: (row, columnId, filter) => filter === "ALL" || (filter === "Advanced") === row.getValue(columnId),
    },
    {
        id: "lastLogin",
        header: t("companies.members_table.last_login"),
        accessorFn: (row) =>
            row.membershipType === "Member" &&
            row.member.lastActivity &&
            formatShortDate(dayjs(row.member.lastActivity), format),
        minSize: 50,
        size: 50,
        enableColumnFilter: false,
    },
];

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canEnableBetaFeatures", "canAssignManagerRole"]);
